/**
 * Die 14 Kennzahlen aus CLAUDE.md — reine Rechnung, keine Oberflaeche.
 *
 * Alle Werte kommen aus den Kopfdaten. Die Tiefenprofile werden dafuer nie
 * angefasst: 995 Kopfdatensaetze rechnen sich in Millisekunden, eine Million
 * Messpunkte nicht.
 *
 * Zwei Haltungen stecken darin:
 *
 * * **Fehlende Werte werden gezaehlt, nicht versteckt.** Nur 341 der 995
 *   Tauchgaenge haben eine Durchschnittstiefe. Ein Mittelwert ohne diese
 *   Angabe waere eine Scheingenauigkeit, deshalb steht die Abdeckung dabei.
 * * **Auffaellige Werte bleiben stehen.** Der tiefste Tauchgang im Bestand
 *   misst laut Quelle 300 m — ein Tippfehler von 2017. Die Statistik zeigt ihn
 *   samt Markierung, statt ihn stillschweigend zu unterschlagen.
 */

import { ortszeit } from './format.js';
import { buddyNamen, normalisiere } from './suche.js';

const KLASSENBREITE = 5;
const TIEFSTE_KLASSE = 60;

/** Der lokale Tag als `YYYY-MM-DD` — nicht der UTC-Tag. */
function ortstag(tg) {
  const zeit = ortszeit(tg);
  return zeit ? zeit.toISOString().slice(0, 10) : null;
}

/** Fuehrt Datumsspanne und Tiefenrekord einer Gruppe mit. */
function merkeSpanne(gruppe, tg) {
  const tag = ortstag(tg);
  if (tag) {
    if (gruppe.von === null || tag < gruppe.von) gruppe.von = tag;
    if (gruppe.bis === null || tag > gruppe.bis) gruppe.bis = tag;
  }
  const tiefe = tg.max_depth_m;
  if (typeof tiefe === 'number' && (gruppe.tiefste === null || tiefe > gruppe.tiefste)) {
    gruppe.tiefste = tiefe;
  }
}

/** Die haeufigste Schreibweise; bei Gleichstand die zuerst gesehene. */
function haeufigste(zaehler) {
  let treffer = null;
  let bester = -1;
  for (const [wert, anzahl] of zaehler) {
    if (anzahl > bester) {
      treffer = wert;
      bester = anzahl;
    }
  }
  return treffer;
}

const zahlen = (liste, feld) => liste
  .map((tg) => tg[feld])
  .filter((wert) => typeof wert === 'number' && !Number.isNaN(wert));

const summe = (werte) => werte.reduce((a, b) => a + b, 0);
const mittel = (werte) => (werte.length ? summe(werte) / werte.length : null);

/**
 * Wassertemperatur eines Tauchgangs.
 *
 * Nur Garmin misst Minimum, Mittel und Maximum; Diving Log kennt einen einzigen
 * Wert. Wer allein auf `water_temp_min_c` schaut, rechnet ueber die 358
 * Garmin-Tauchgaenge — alle tropisch — und meldet 16 °C als kaeltesten
 * Tauchgang, waehrend im Bestand Schweizer Seen mit 5 °C stehen.
 */
export function wassertemperatur(tg, richtung) {
  const kandidaten = richtung === 'min'
    ? [tg.water_temp_min_c, tg.water_temp_avg_c]
    : [tg.water_temp_max_c, tg.water_temp_avg_c];
  for (const wert of kandidaten) {
    if (typeof wert === 'number' && !Number.isNaN(wert)) return wert;
  }
  return null;
}

function grenzwert(liste, wertVon, richtung) {
  const hole = typeof wertVon === 'function' ? wertVon : (tg) => tg[wertVon];
  let treffer = null;
  let bester = null;
  for (const tg of liste) {
    const wert = hole(tg);
    if (typeof wert !== 'number' || Number.isNaN(wert)) continue;
    if (bester === null || (richtung === 'max' ? wert > bester : wert < bester)) {
      treffer = tg;
      bester = wert;
    }
  }
  return treffer;
}

export function jahresverteilung(liste) {
  const proJahr = new Map();
  for (const tg of liste) {
    const jahr = String(tg.start_utc ?? '').slice(0, 4);
    if (jahr) proJahr.set(jahr, (proJahr.get(jahr) ?? 0) + 1);
  }
  return [...proJahr.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

/** Tiefen in 5-Meter-Klassen; alles ab 60 m in einer Klasse. */
export function tiefenverteilung(liste) {
  const klassen = new Map();
  for (const tiefe of zahlen(liste, 'max_depth_m')) {
    const unten = Math.min(Math.floor(tiefe / KLASSENBREITE) * KLASSENBREITE, TIEFSTE_KLASSE);
    klassen.set(unten, (klassen.get(unten) ?? 0) + 1);
  }
  const hoechste = Math.max(...klassen.keys(), 0);
  const reihe = [];
  for (let unten = 0; unten <= hoechste; unten += KLASSENBREITE) {
    reihe.push({
      von: unten,
      bis: unten >= TIEFSTE_KLASSE ? null : unten + KLASSENBREITE,
      anzahl: klassen.get(unten) ?? 0,
    });
  }
  return reihe;
}

export function rangliste(liste, anzahl = 20) {
  const proPlatz = new Map();
  for (const tg of liste) {
    if (!tg.site_name) continue;
    proPlatz.set(tg.site_name, (proPlatz.get(tg.site_name) ?? 0) + 1);
  }
  return [...proPlatz.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, anzahl)
    .map(([name, treffer]) => ({ name, anzahl: treffer }));
}

/** Laender werden ueber den ISO-Code gezaehlt: "Mexico" und "Mexiko" sind eines. */
export function laender(liste, plaetze = []) {
  const nachId = new Map(plaetze.map((p) => [p.id, p]));
  const codes = new Set();
  const ohneCode = new Set();
  for (const tg of liste) {
    const platz = nachId.get(tg.site_id);
    if (platz?.country_code) codes.add(platz.country_code);
    else if (platz?.country || tg.country) ohneCode.add(platz?.country ?? tg.country);
  }
  return { anzahl: codes.size + ohneCode.size, codes: [...codes].sort(), ohneCode: [...ohneCode] };
}

export function berechne(liste, plaetze = []) {
  const tiefen = zahlen(liste, 'max_depth_m');
  const schnitttiefen = zahlen(liste, 'avg_depth_m');
  const dauern = zahlen(liste, 'duration_s');
  const kalt = liste.map((tg) => wassertemperatur(tg, 'min')).filter((w) => w !== null);
  const warm = liste.map((tg) => wassertemperatur(tg, 'max')).filter((w) => w !== null);
  const zeiten = liste.map((tg) => tg.start_utc).filter(Boolean).sort();

  return {
    anzahl: liste.length,
    jahre: jahresverteilung(liste),
    tiefen: tiefenverteilung(liste),
    tiefster: grenzwert(liste, 'max_depth_m', 'max'),
    laengster: grenzwert(liste, 'duration_s', 'max'),
    kaeltester: grenzwert(liste, (tg) => wassertemperatur(tg, 'min'), 'min'),
    waermster: grenzwert(liste, (tg) => wassertemperatur(tg, 'max'), 'max'),
    schnittTiefe: mittel(schnitttiefen),
    schnittTiefeVon: schnitttiefen.length,
    schnittMaxTiefe: mittel(tiefen),
    schnittDauer: mittel(dauern),
    gesamtdauer: summe(dauern),
    plaetze: new Set(liste.map((tg) => tg.site_name).filter(Boolean)).size,
    laender: laender(liste, plaetze),
    erster: zeiten[0] ?? null,
    letzter: zeiten[zeiten.length - 1] ?? null,
    mitProfil: liste.filter((tg) => tg.has_profile).length,
    zuPruefen: liste.filter((tg) => tg.needs_review).length,
    rangliste: rangliste(liste),
    kaelteste: kalt.length ? Math.min(...kalt) : null,
    waermste: warm.length ? Math.max(...warm) : null,
  };
}

/**
 * Buddys nach Haeufigkeit.
 *
 * Gezaehlt wird ueber `buddyNamen()` — dieselbe Ableitung, die auch der Filter
 * benutzt. Sonst stuende in der Rangliste eine Zahl, die die Liste danach
 * nicht bestaetigt, und das waere schlimmer als keine Rangliste.
 *
 * Zusammengefasst wird ueber die normalisierte Schreibweise: „Sandra" und
 * „sandra" sind ein Mensch. Angezeigt wird die haeufigste Schreibweise, nicht
 * die erste — wer einmal daneben tippt, soll damit nicht die Ueberschrift
 * bestimmen.
 */
export function nachBuddy(liste) {
  const proBuddy = new Map();
  let ohneBuddy = 0;
  for (const tg of liste) {
    const namen = buddyNamen(tg);
    if (namen.length === 0) {
      ohneBuddy += 1;
      continue;
    }
    for (const name of namen) {
      const schluessel = normalisiere(name);
      let b = proBuddy.get(schluessel);
      if (!b) {
        b = { name, anzahl: 0, schreibweisen: new Map(), von: null, bis: null, tiefste: null };
        proBuddy.set(schluessel, b);
      }
      b.anzahl += 1;
      b.schreibweisen.set(name, (b.schreibweisen.get(name) ?? 0) + 1);
      merkeSpanne(b, tg);
    }
  }
  const buddys = [...proBuddy.values()].map((b) => ({
    name: haeufigste(b.schreibweisen),
    anzahl: b.anzahl,
    von: b.von,
    bis: b.bis,
    tiefste: b.tiefste,
  }));
  buddys.sort((a, b) => b.anzahl - a.anzahl || a.name.localeCompare(b.name));
  return { buddys, ohneBuddy, mitBuddy: liste.length - ohneBuddy };
}

/**
 * Reisen — chronologisch, nicht nach Haeufigkeit.
 *
 * Eine Reise ist ein Ereignis und keine Vorliebe: Wer wissen will, wie die
 * Malediven 2019 waren, sucht sie nicht auf Platz elf einer Rangliste, sondern
 * dort, wo sie zeitlich hingehoert. Deshalb steht die juengste oben.
 *
 * Die Tage kommen aus der **Ortszeit** — nach UTC gruppiert fielen 28 der 995
 * Tauchgaenge in den falschen Tag, und eine Reise haette einen Tag zu viel.
 */
export function nachReise(liste) {
  const proReise = new Map();
  let ohneReise = 0;
  for (const tg of liste) {
    const name = String(tg.trip_name ?? '').trim();
    if (!name) {
      ohneReise += 1;
      continue;
    }
    let r = proReise.get(normalisiere(name));
    if (!r) {
      r = {
        name,
        anzahl: 0,
        grundzeit: 0,
        tage: new Set(),
        plaetze: new Set(),
        von: null,
        bis: null,
        tiefste: null,
      };
      proReise.set(normalisiere(name), r);
    }
    r.anzahl += 1;
    if (typeof tg.duration_s === 'number') r.grundzeit += tg.duration_s;
    if (tg.site_name) r.plaetze.add(tg.site_name);
    const tag = ortstag(tg);
    if (tag) r.tage.add(tag);
    merkeSpanne(r, tg);
  }
  const reisen = [...proReise.values()].map((r) => ({
    name: r.name,
    anzahl: r.anzahl,
    grundzeit: r.grundzeit,
    tage: r.tage.size,
    plaetze: r.plaetze.size,
    von: r.von,
    bis: r.bis,
    tiefste: r.tiefste,
  }));
  // Juengste zuerst; Reisen ohne datierten Tauchgang ans Ende.
  reisen.sort((a, b) => (b.bis ?? '').localeCompare(a.bis ?? '') || a.name.localeCompare(b.name));
  return { reisen, ohneReise, aufReise: liste.length - ohneReise };
}
